import React, { useState } from "react";
import { Link, Redirect, useLocation } from "react-router-dom";
import { Button, Text, TextField } from "@merge-api/merge-javascript-shared";

import { hasAuthToken } from "../../../api-client/APIClient";
import { LOGIN_PATH } from "../../../router/RouterUtils";
import { REGEX_EMAIL_ADDRESS } from "../../shared/utils/SharedComponentUtils";
import AuthCard from "./shared/AuthCard";
import AuthPageWrapper from "./shared/AuthPageWrapper";

interface AuthLayoutProps {
  title?: string;
  description?: JSX.Element | string;
  finePrint?: JSX.Element | string;
  hideBackToLogin?: boolean;
  children?: React.ReactNode;
}

/**
 * Render an auth-style page ("Forgot password", "Check your email", "No account found"...).
 * Uses the same card chrome as /signup and /login so the unauthenticated surface is consistent.
 */
export const AuthLayout = ({
  title,
  description,
  finePrint,
  hideBackToLogin,
  children,
}: AuthLayoutProps) => {
  if (hasAuthToken()) {
    return <Redirect to="/" />;
  }
  return (
    <AuthPageWrapper>
      <AuthCard className="p-6 flex flex-col gap-6">
        {(title || description) && (
          <div className="flex flex-col gap-2">
            {title && <Text variant="h3">{title}</Text>}
            {description && <Text variant="md">{description}</Text>}
          </div>
        )}
        {children}
        {finePrint && (
          <Text variant="sm" className="text-center text-gray-40">
            {finePrint}
          </Text>
        )}
        {!hideBackToLogin && (
          <div className="text-center">
            <Link to={LOGIN_PATH} className="text-sm text-blue-60 underline hover:text-blue-80">
              Back to login
            </Link>
          </div>
        )}
      </AuthCard>
    </AuthPageWrapper>
  );
};

interface EmailFormData {
  email: string;
}
type SetErrorMessageFunction = (errorMessage?: string) => void;
type SetIsLoadingFunction = (isLoading?: boolean) => void;
export type OnEmailFormSubmitFunction = (
  data: EmailFormData,
  setEmailErrorMessage: SetErrorMessageFunction,
  setEmailFormIsLoading: SetIsLoadingFunction,
) => void;

interface AuthEmailFormProps extends AuthLayoutProps {
  onEmailFormSubmit: OnEmailFormSubmitFunction;
  submitText?: string;
}

/**
 * Auth screen with a single email input + submit button. Used by /password/send-reset
 * and /login/send-sso-url.
 */
export const AuthEmailForm = ({
  title,
  description,
  finePrint,
  onEmailFormSubmit,
  submitText = "Submit",
}: AuthEmailFormProps) => {
  const location = useLocation<{ email?: string } | undefined>();
  const [email, setEmail] = useState(location.state?.email ?? "");
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !REGEX_EMAIL_ADDRESS.test(email)) {
      setErrorMessage("Please enter a valid email address.");
      return;
    }
    setErrorMessage(undefined);
    onEmailFormSubmit(
      { email },
      (msg) => setErrorMessage(msg),
      (loading) => setIsLoading(Boolean(loading)),
    );
  };

  return (
    <AuthLayout title={title} description={description} finePrint={finePrint}>
      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-6">
        <TextField
          name="email"
          type="email"
          labelTitle="Email"
          placeholder="Email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (errorMessage) setErrorMessage(undefined);
          }}
          error={!!errorMessage}
          errorText={errorMessage}
        />
        <Button type="submit" fullWidth loading={isLoading}>
          {submitText}
        </Button>
      </form>
    </AuthLayout>
  );
};
